import React, { useState, useEffect, useCallback } from 'react';
import { CalendarRange, ArrowDown, ArrowUp, BarChart3 } from 'lucide-react';
import { apiFetch } from '../utils/apiClient';
import { todayStr, firstOfMonthStr } from '../utils/formatDate';

const formatMinutes = (mins) => {
  if (!mins) return null;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const firstOfLastMonthStr = () => {
  const [year, month] = firstOfMonthStr().split('-').map(Number);
  const prevMonth = month === 1 ? 12 : month - 1;
  const prevYear = month === 1 ? year - 1 : year;
  return `${prevYear}-${String(prevMonth).padStart(2, '0')}-01`;
};

const lastOfLastMonthStr = () => {
  const [year, month] = firstOfMonthStr().split('-').map(Number);
  // Day 0 of this month = last day of the previous month. Pure UTC calendar
  // arithmetic, no timezone conversion involved.
  const d = new Date(Date.UTC(year, month - 1, 0));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
};

// Each column: how to read its value for sorting, and how to render its cell.
// `count` is the headline number, `mins`/`money` the muted detail under it.
const COLUMNS = [
  { key: 'employeeName', label: 'Employee', align: 'left', text: true },
  { key: 'daysRecorded', label: 'Days Worked', count: r => r.daysRecorded },
  { key: 'absentDays', label: 'Absent', count: r => r.absentDays, tone: 'bad' },
  { key: 'lateDays', label: 'Late', count: r => r.lateDays, mins: r => r.lateMinutes, tone: 'bad' },
  { key: 'longBreakDays', label: 'Long Break', count: r => r.longBreakDays, mins: r => r.breakMinutes, tone: 'bad' },
  { key: 'earlyDepartureDays', label: 'Left Early', count: r => r.earlyDepartureDays, mins: r => r.earlyDepartureMinutes, tone: 'bad' },
  { key: 'leaveDays', label: 'Leave', count: r => r.leaveDays, extra: r => (r.excusedDays ? `+${r.excusedDays} excused` : null) },
  { key: 'overtimeMinutes', label: 'Overtime', mins: r => r.overtimeMinutes, money: r => r.overtimeAmount, tone: 'good' },
  { key: 'deductionAmount', label: 'Deductions', money: r => r.deductionAmount, tone: 'bad' }
];

const Reports = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState({ from: firstOfMonthStr(), to: todayStr() });
  const [sort, setSort] = useState({ key: 'lateDays', dir: 'desc' });
  const [showInactive, setShowInactive] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (range.from) params.set('from', range.from);
      if (range.to) params.set('to', range.to);
      const response = await apiFetch(`/api/attendance/summary?${params.toString()}`);
      if (response.ok) setRows(await response.json());
    } catch (e) {
      console.error('Error fetching attendance summary');
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const hasActivity = (r) =>
    r.daysRecorded || r.leaveDays || r.excusedDays || r.overtimeMinutes || r.deductionAmount || r.absentDays;

  const visibleRows = rows.filter(r => showInactive || hasActivity(r));

  const sorted = [...visibleRows].sort((a, b) => {
    const col = COLUMNS.find(c => c.key === sort.key);
    let av, bv;
    if (col?.text) {
      av = a.employeeName.toLowerCase();
      bv = b.employeeName.toLowerCase();
      return sort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    }
    av = a[sort.key] ?? 0;
    bv = b[sort.key] ?? 0;
    // Stable tie-break by name, so equal values don't jump around between renders.
    if (av === bv) return a.employeeName.localeCompare(b.employeeName);
    return sort.dir === 'asc' ? av - bv : bv - av;
  });

  const toggleSort = (key) => {
    setSort(prev => prev.key === key
      // Names read naturally A→Z first; metrics are most useful worst-first.
      ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
      : { key, dir: key === 'employeeName' ? 'asc' : 'desc' });
  };

  const toneColor = (tone, value) => {
    if (!value) return 'var(--text-muted)';
    return tone === 'bad' ? 'var(--danger-color)' : tone === 'good' ? 'var(--success-color)' : 'var(--text-main)';
  };

  return (
    <div className="reports-page">
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700' }}>Staff Reports</h1>
        <p style={{ color: 'var(--text-muted)' }}>Attendance patterns per employee — click any column to rank by it</p>
      </div>

      <div className="card" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', padding: '14px 20px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--text-muted)', fontWeight: '500' }}>
          <CalendarRange size={16} />
          Date Range
        </div>
        <input
          type="date"
          className="input-field"
          style={{ padding: '8px 10px', fontSize: '13px', width: 'auto' }}
          max={range.to || todayStr()}
          value={range.from}
          onChange={e => setRange(prev => ({ ...prev, from: e.target.value }))}
        />
        <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>to</span>
        <input
          type="date"
          className="input-field"
          style={{ padding: '8px 10px', fontSize: '13px', width: 'auto' }}
          min={range.from}
          max={todayStr()}
          value={range.to}
          onChange={e => setRange(prev => ({ ...prev, to: e.target.value }))}
        />
        <button
          className="btn"
          style={{ padding: '8px 14px', fontSize: '13px', background: 'var(--bg-color)' }}
          onClick={() => setRange({ from: firstOfMonthStr(), to: todayStr() })}
        >
          This Month
        </button>
        <button
          className="btn"
          style={{ padding: '8px 14px', fontSize: '13px', background: 'var(--bg-color)' }}
          onClick={() => setRange({ from: firstOfLastMonthStr(), to: lastOfLastMonthStr() })}
        >
          Last Month
        </button>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--text-muted)', marginLeft: 'auto', cursor: 'pointer' }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Show employees with no activity
        </label>
      </div>

      <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
        <div className="table-scroll">
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '960px' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--bg-color)', borderBottom: '1px solid var(--border-color)' }}>
                {COLUMNS.map(col => {
                  const active = sort.key === col.key;
                  return (
                    <th
                      key={col.key}
                      onClick={() => toggleSort(col.key)}
                      style={{
                        padding: '16px', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
                        textAlign: col.align || 'center',
                        color: active ? 'var(--primary-color)' : 'var(--text-main)'
                      }}
                      title={`Sort by ${col.label}`}
                    >
                      {col.label}
                      {active && (sort.dir === 'desc'
                        ? <ArrowDown size={13} style={{ marginLeft: '4px', verticalAlign: 'middle' }} />
                        : <ArrowUp size={13} style={{ marginLeft: '4px', verticalAlign: 'middle' }} />)}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sorted.map(r => (
                <tr key={r.employeeId} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  {COLUMNS.map(col => {
                    if (col.text) {
                      return <td key={col.key} style={{ padding: '16px', fontWeight: '500' }}>{r.employeeName}</td>;
                    }
                    const count = col.count ? col.count(r) : null;
                    const mins = col.mins ? col.mins(r) : null;
                    const money = col.money ? col.money(r) : null;
                    const extra = col.extra ? col.extra(r) : null;
                    const headline = count !== null
                      ? (count ? `${count} ${count === 1 ? 'day' : 'days'}` : '—')
                      : (mins ? formatMinutes(mins) : (money ? `Rs${money.toFixed(2)}` : '—'));
                    const isEmpty = headline === '—';
                    const detail = count !== null
                      ? (mins ? formatMinutes(mins) : extra)
                      : (mins && money ? `Rs${money.toFixed(2)}` : null);
                    return (
                      <td key={col.key} style={{ padding: '16px', textAlign: 'center' }}>
                        <div style={{ fontWeight: isEmpty ? '400' : '600', color: toneColor(col.tone, !isEmpty) }}>
                          {headline}
                        </div>
                        {detail && (
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{detail}</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {!loading && sorted.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    <BarChart3 size={20} style={{ marginBottom: '8px', opacity: 0.5 }} />
                    <div>No attendance activity recorded in this date range.</div>
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={COLUMNS.length} style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '12px' }}>
        A working day with no punches and no leave marked counts as <strong>Absent</strong> and docks
        a full day's pay — mark it as Leave on the Attendance page to reverse that. Absence tracking
        starts from an employee's first-ever punch, skips their weekly off days, and skips any day
        the fingerprint device recorded nothing at all (treated as the device being down or the
        restaurant closed, rather than everyone being absent).
      </p>
    </div>
  );
};

export default Reports;
